import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import BaseListingRendererWithPlugins from 'nav/expanded/BaseListingRendererWithPlugins';
import {
  ColumnAccessorKey,
  ColumnConfiguration,
  ListingRendererType,
} from 'nav/expanded/ListingRendererConstants';
import * as TestOrderContextMenu from 'sample-tests/search/TestOrderContextMenu';
import {
  renderTestOrderAssayChip,
  renderTestOrderContainerChip,
  renderTestOrderSample,
  renderTestOrderTestDefinitionChip,
  renderTestOrderWorksheetChip,
} from 'sample-tests/ui/utils/testOrderChipsUtil';
import {TestOrderStatusDisplay} from 'sample-tests/ui/utils/TestOrderStatusUtil';
import {getRowHeight, listSortedTagColumns, mapTagsToColumns} from 'schemas/tags/ExpandedTableTagUtil';
import {BaseSchema} from 'schemas/tags/TableTagUtil';
import {TagForTableDisplay} from 'schemas/tags/TagValue';
import {SortKey} from 'search/SearchConstants';
import {getColumnHeaderFromSortKey} from 'search/SortUtil';
import {
  maybeGetStudiesListingColumn,
  StudyableItemListingExtras,
} from 'studies/studyable/StudiesListingColumn';
import {ChipSize} from 'ui-library/chips/Chip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {ScrollableTableColumn, TABLE_ROW_BODY_HEIGHT} from 'ui-library/scrollable/ScrollableTableConstants';
import {DatetimeTableCell} from 'ui-library/scrollable/table-cells/DatetimeTableCell';
import {omit} from 'util/omitKey';
import {BasicEvent} from 'util/Types';

import {TestOrderListingGraphQL} from '../../../graphql-fragments/sample_tests/__generated/TestOrderListingGraphQL';

type MaybeInaccessibleResult = TestOrderListingGraphQL['result'];
export type ResultNode = ExtractByTypename<MaybeInaccessibleResult, 'ResultNode'>;
interface NonNullableResultNode extends Omit<ResultNode, 'schema'> {
  schema: BaseSchema;
}

export interface TestOrderListingExtras extends StudyableItemListingExtras {}

export default class TestOrderListingRenderer extends BaseListingRendererWithPlugins<ListingRendererType.TEST_ORDER> {
  listingRendererType = ListingRendererType.TEST_ORDER;

  renderIcon(): JSX.Element {
    return <BenchlingIcon icon={ModelTypeConstants.TEST_ORDER} className="scrollableTable-cell-icon" />;
  }

  getNonPluginColumnConfigurations(
    items: Array<TestOrderListingGraphQL>,
    {itemStudyAttributes}: TestOrderListingExtras = {}
  ): Array<ColumnConfiguration<TestOrderListingGraphQL>> {
    const maybeStudiesColumn = maybeGetStudiesListingColumn<TestOrderListingGraphQL>(itemStudyAttributes);

    let columns: Array<ColumnConfiguration<TestOrderListingGraphQL>> = [
      {
        column: {
          accessorKey: ColumnAccessorKey.NAME,
          header: 'Name',
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_NAME_KEY,
        },
        render: (item) => item.name,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.SAMPLE,
          header: 'Sample',
          flexGrow: 1,
        },
        render: (item) => renderTestOrderSample(item),
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.STATUS,
          header: 'Status',
          flexGrow: 1,
        },
        render: (item) => <TestOrderStatusDisplay status={item.status} />,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.ANALYSIS_COMMENT,
          header: 'Analysis comment',
          flexGrow: 1,
        },
        render: (item) => item.analysisComment,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.CONTAINER,
          header: 'Container',
          flexGrow: 1,
        },
        render: (item) => renderTestOrderContainerChip(item, ChipSize.MINI),
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.DILUTION_FACTOR,
          header: 'Dilution factor',
          flexGrow: 1,
        },
        render: (item) => item.dilutionFactor || '',
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.TEST_DEFINITION,
          header: 'Test definition',
          flexGrow: 1,
        },
        render: (item) => renderTestOrderTestDefinitionChip(item, ChipSize.MINI),
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.SAMPLE_DEFINITIONS,
          header: 'Sample definitions',
          flexGrow: 1,
        },
        render: (item) => {
          if (
            !isInaccessible(item.sample) &&
            item.sample.__typename === 'BasicFolderItemNode' &&
            item.sample.sampleDefinitionLinks
          ) {
            const sampleDefinitionLabels: Array<string> = item.sample.sampleDefinitionLinks.reduce(
              (result, sampleDefinitionLink) => {
                if (
                  sampleDefinitionLink.sampleDefinition &&
                  sampleDefinitionLink.sampleDefinition.__typename === 'SampleDefinitionNode'
                ) {
                  result.push(sampleDefinitionLink.sampleDefinition.label);
                }
                return result;
              },
              [] as Array<string>
            );
            return sampleDefinitionLabels;
          }
          return '';
        },
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.ASSAY_TEMPLATE,
          header: 'Assay',
          flexGrow: 1,
        },
        render: (item) => renderTestOrderAssayChip(item, ChipSize.MINI),
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.WORKSHEET,
          header: 'Worksheet',
          flexGrow: 1,
        },
        render: (item) => renderTestOrderWorksheetChip(item, ChipSize.MINI),
      },
      ...(maybeStudiesColumn ? [maybeStudiesColumn] : []),
      {
        column: {
          accessorKey: ColumnAccessorKey.CREATED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_CREATED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_CREATED_AT_KEY,
        },
        render: (item) => <DatetimeTableCell datetime={item.createdAt} showTimeInCell={false} />,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.MODIFIED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_MODIFIED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_MODIFIED_AT_KEY,
        },
        render: (item) => <DatetimeTableCell datetime={item.modifiedAt} showTimeInCell={false} />,
      },
    ];

    columns = this._updateWithResultTagColumns(items, columns);
    return columns;
  }

  _updateWithResultTagColumns(
    items: Array<TestOrderListingGraphQL>,
    columns: Array<ColumnConfiguration<TestOrderListingGraphQL>>
  ): Array<ColumnConfiguration<TestOrderListingGraphQL>> {
    const results = this.extractResultsInfo(items);

    const tagColumns = listSortedTagColumns(results);

    if (tagColumns.length) {
      columns = columns.concat(
        tagColumns.map((column: ScrollableTableColumn) => ({
          column: {...column, isSortable: false},
          render: (item: TestOrderListingGraphQL) => {
            const tagsByColumn = mapTagsToColumns({
              tags: this.getResultTags(item),
              tagColumns: [column],
            });
            return tagsByColumn.tagFields[column.accessorKey];
          },
        }))
      );
    }
    return columns;
  }

  extractResultsInfo(items: Array<TestOrderListingGraphQL>): Array<NonNullableResultNode> {
    const results: Array<NonNullableResultNode> = [];

    items.forEach((item) => {
      if (item.result != null && !isInaccessible(item.result) && item.result.schema != null) {
        results.push({
          ...item.result,
          schema: {
            ...omit(item.result.schema, 'fieldsJson'),
            fields: item.result.schema.fieldsJson,
          },
        });
      }
    });
    return results;
  }

  getResultTags(item: TestOrderListingGraphQL): Array<TagForTableDisplay> {
    if (item.result != null && !isInaccessible(item.result)) {
      return item.result.tagsJson ?? [];
    }
    return [];
  }

  getNonPluginRowContentHeight(item: TestOrderListingGraphQL): number {
    const tags = this.getResultTags(item);
    if (tags.length > 0) {
      return getRowHeight(tags);
    }
    return TABLE_ROW_BODY_HEIGHT;
  }

  openContextMenu(item: TestOrderListingGraphQL, e: BasicEvent, extras: unknown): void {
    TestOrderContextMenu.open(e.nativeEvent, item);
  }
}
